/*
 * A faixa de cadastro do fim do artigo: chamada, texto de apoio e o formulário embutido.
 *
 * O formulário é o `<auth-functional>` — a MESMA tag que a página já usa —, e não o container
 * React importado direto: o `authfunctional.module.js` continua sendo carregado pela página
 * (a modal depende dele), então importar a árvore aqui duplicaria o maior bundle do site
 * dentro deste módulo. Pelo mesmo motivo não se usa o caminho de `CtaAuthForm.tsx`, que existe
 * justamente para páginas onde aquele módulo NÃO é carregado.
 *
 * QUEM MONTA: o próprio `authfunctional.module.js`. Ele roda `renderAuth()` no escopo do módulo
 * e o `getRevisionContent` dele vem DEPOIS deste no documento — script de módulo é deferido e
 * executa na ordem do documento, então a tag já existe quando ele varre a página. O efeito
 * abaixo cobre o caso inverso (bundle já carregado antes deste, por cache ou reordenação):
 * se `renderAuth` já existe, a varredura dele já passou e é preciso montar à mão.
 *
 * SEM `data-custom-style`, de propósito: o padrão do módulo é o tema LIGHT. O `HomeInterLP`
 * que a modal usa tem `background: transparent` (`CustomStyles.ts:54`) e aqui deixaria o
 * formulário transparente sobre a foto escura.
 */
import React, { useEffect, useRef } from 'react';

/*
 * A tag sai por `createElement` e não como JSX: `<auth-functional>` não está na tabela de
 * elementos intrínsecos do TSX, e declará-la exigiria `declare global { namespace JSX }` —
 * que o lint do projeto proíbe (`@typescript-eslint/no-namespace`).
 */
const AUTH_FUNCTIONAL_TAG = 'auth-functional';

interface IAuthSectionProps {
    title: string;
    description: string;
    /** Passo inicial do `AuthFunctional` (`SIGNUP:BIRTH_DATA`). */
    authStep: string;
    /** `MAINTENANCE_MODE` do legado, repassado cru. */
    maintenance: string;
}

const AuthSection: React.FC<IAuthSectionProps> = ({ title, description, authStep, maintenance }) => {
    const formRef = useRef<HTMLElement>(null);

    useEffect(() => {
        /* Sem alvo de propósito: nesta branch `renderAuth` ainda não aceita um, e no momento
         * em que este efeito roda o único `<auth-functional>` da página é este — o da modal só
         * nasce depois, no `document.styledModal` do fim do body. */
        if (formRef.current && typeof document.renderAuth === 'function') document.renderAuth();
    }, []);

    return (
        <>
            <h2 className="auth-section__title">{title}</h2>
            <p className="auth-section__description">{description}</p>

            <div className="auth-section__form">
                {React.createElement(AUTH_FUNCTIONAL_TAG, {
                    ref: formRef,
                    'data-initial-step': authStep,
                    'data-maintenance': maintenance
                })}
            </div>
        </>
    );
};

export default AuthSection;
